import React, { useState, useEffect } from 'react';
import api, { formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { Wallet, ArrowDownLeft, ArrowUpRight, TrendingUp } from 'lucide-react';

export default function AdvanceSummary() {
  const [data, setData] = useState({ parties: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      const res = await api.get('/reports/advance-summary', { params });
      setData(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [typeFilter]);
  const s = data.summary || {};

  return (
    <div data-testid="advance-summary-page" className="space-y-6 max-w-6xl mx-auto">
      <div><h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Advance Summary</h1>
        <p className="text-sm text-slate-500 font-body">Party-wise advance tracking and balances</p></div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Total Received" value={s.total_received || 0} icon={ArrowDownLeft} color="emerald" />
        <Card label="Balance (Received)" value={s.total_balance_received || 0} icon={Wallet} color="blue" />
        <Card label="Total Paid" value={s.total_paid || 0} icon={ArrowUpRight} color="amber" />
        <Card label="Balance (Paid)" value={s.total_balance_paid || 0} icon={TrendingUp} color="violet" />
      </div>

      <div className="flex items-center gap-3">
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="form-input w-40" data-testid="summary-type-filter">
          <option value="">All Types</option><option value="received">Received</option><option value="paid">Paid</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="data-table w-full" data-testid="advance-summary-table">
          <thead><tr><th>Party Name</th><th>Type</th><th className="text-right">Total Amount</th><th className="text-right">Adjusted</th><th className="text-right">Balance</th><th className="text-right">Count</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="6" className="text-center py-8 text-slate-400">Loading...</td></tr> :
             (data.parties || []).length === 0 ? <tr><td colSpan="6" className="text-center py-8 text-slate-400">No advances found</td></tr> :
             (data.parties || []).map((p, i) => (
              <tr key={i} className="hover:bg-slate-50/50" data-testid={`summary-row-${i}`}>
                <td className="font-medium text-slate-800">{p.party_name}</td>
                <td><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.type === 'received' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{p.type}</span></td>
                <td className="text-right font-mono">{formatCurrency(p.total_amount)}</td>
                <td className="text-right font-mono text-slate-500">{formatCurrency(p.total_adjusted)}</td>
                <td className="text-right font-mono font-medium">{formatCurrency(p.total_balance)}</td>
                <td className="text-right text-slate-600">{p.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ label, value, icon: Icon, color }) {
  const colors = { emerald: 'bg-emerald-50 text-emerald-700', amber: 'bg-amber-50 text-amber-700', blue: 'bg-blue-50 text-blue-700', violet: 'bg-violet-50 text-violet-700' };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={`card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2"><div className={`p-1.5 rounded ${colors[color]}`}><Icon className="w-4 h-4" /></div><span className="text-xs text-slate-500 font-medium">{label}</span></div>
      <p className="font-mono font-bold text-xl text-slate-900">Rs. {formatCurrency(value)}</p>
    </div>
  );
}
